import * as AvailabilityModels from "../availability-status/availability-models";
import { LocalizationApi } from "../../../ShellCommon/localization/localization";
import * as ChannelsModels from "../agent-channels/channels-models";
import { CantileverStateConstants } from "../availability-status/availability-constants";

// -- Interfaces --

export interface PresenceState {
  reason: number;
  displayName: string;
  glyph: string;
  cantileverState: AvailabilityModels.CantileverState;
  showInMenu: boolean;

  addSubState(subState: PresenceState): PresenceState;
  getSubStates(): PresenceState[];
  resolveLoc(appId: string, locApi: LocalizationApi): void;
}

export interface PresenceStatesCollection {
  allStates: PresenceState[];
}

// -- Implementations --

export class PresenceStateImplementation implements PresenceState {
  private stateName: string;
  private subStates: PresenceState[] = [];

  // -- Constructor --
  constructor(
    private locId: string,
    private glyphPrv: string,
    private cantileverStatePrv: AvailabilityModels.CantileverState,
    private showInMenuPrv = true
  ) {}

  // -- Properties --

  get reason(): number {
    return this.cantileverState.reason;
  }

  private _displayName: string;
  get displayName(): string {
    return this._displayName;
  }

  get glyph(): string {
    return this.glyphPrv;
  }

  get cantileverState(): AvailabilityModels.CantileverState {
    return this.cantileverStatePrv;
  }

  get showInMenu(): boolean {
    return this.showInMenuPrv;
  }

  // -- Public methods --

  public addSubState(subState: PresenceState): PresenceState {
    if (subState == undefined) {
      return;
    }

    this.subStates.push(subState);
    return subState;
  }

  public getSubStates(): PresenceState[] {
    return this.subStates;
  }

  public resolveLoc(appId: string, locApi: LocalizationApi) {
    if (locApi == undefined || appId == undefined) {
      return;
    }

    this._displayName = locApi.getLocalizedString(appId, this.locId);

    this.subStates.forEach((subState) => {
      subState.resolveLoc(appId, locApi);
    });
  }
}

export abstract class PresenceStateCollectionBase implements PresenceStatesCollection {
  private states: PresenceState[] = [];

  protected constructor() {}

  // -- Properties --
  get allStates(): PresenceState[] {
    return this.states;
  }

  // -- Protected methods --

  protected addPresenceState(state: PresenceState): PresenceState {
    this.states.push(state);
    return state;
  }

  // -- Public methods --

  // Not using a Map here on intention because sub-states could be added after the root is added
  // to the collection
  public findState(modState: ChannelsModels.ModalityAvailability): PresenceState {
    return this.findStateRecursive(this.states, modState.reason);
  }

  public findStateByReason(availabilityReason: any): PresenceState {
    return this.findStateRecursive(this.states, availabilityReason);
  }

  // -- Private methods --

  private findStateRecursive(states: PresenceState[], availabilityReason: any): PresenceState {
    if (states == undefined) {
      return undefined;
    }

    let result: PresenceState = undefined;
    states.some((element, index, array) => {
      if (availabilityReason == element.reason) {
        result = element;
        return true;
      }

      let s: PresenceState = this.findStateRecursive(element.getSubStates(), availabilityReason);
      if (s != undefined) {
        result = s;
        return true;
      }
    });

    return result;
  }

  private resolveLoc(appId: string, locApi: LocalizationApi) {
    this.states.forEach((state) => {
      state.resolveLoc(appId, locApi);
    });
  }
}

export class SdPresenceStatesCollection extends PresenceStateCollectionBase {
  // @ngInject
  constructor(private cantileverStateConstants: CantileverStateConstants) {
    super();
    this.createStates();
  }

  // -- Private methods --

  private createStates() {
    this.addPresenceState(
      new PresenceStateImplementation(
        "_AGENT_DESKTOP_NAV_BAR_PRESENCE_AVAILABLE_",
        "sd-glyph-agent-available",
        this.cantileverStateConstants.AVAILABLE
      )
    );
    this.addPresenceState(
      new PresenceStateImplementation(
        "_AGENT_DESKTOP_NAV_BAR_PRESENCE_NOTAVAILABLE_",
        "sd-glyph-agent-away",
        this.cantileverStateConstants.NOTAVAILABLE,
        false
      )
    );
    this.addPresenceState(
      new PresenceStateImplementation(
        "_AGENT_DESKTOP_NAV_BAR_PRESENCE_BUSY_",
        "sd-glyph-agent-busy",
        this.cantileverStateConstants.BUSY
      )
    );
    let awayState: PresenceState = this.addPresenceState(
      new PresenceStateImplementation(
        "_AGENT_DESKTOP_NAV_BAR_PRESENCE_AWAY_",
        "sd-glyph-agent-away",
        this.cantileverStateConstants.AWAY
      )
    );
    awayState.addSubState(
      new PresenceStateImplementation(
        "_AGENT_DESKTOP_NAV_BAR_PRESENCE_LUNCH_",
        "sd-glyph-agent-away",
        this.cantileverStateConstants.LUNCH
      )
    );
    awayState.addSubState(
      new PresenceStateImplementation(
        "_AGENT_DESKTOP_NAV_BAR_PRESENCE_BREAK_",
        "sd-glyph-agent-away",
        this.cantileverStateConstants.BREAK
      )
    );
    awayState.addSubState(
      new PresenceStateImplementation(
        "_AGENT_DESKTOP_NAV_BAR_PRESENCE_TRAINING_",
        "sd-glyph-agent-away",
        this.cantileverStateConstants.TRAINING
      )
    );
    awayState.addSubState(
      new PresenceStateImplementation(
        "_AGENT_DESKTOP_NAV_BAR_PRESENCE_MEETING_",
        "sd-glyph-agent-away",
        this.cantileverStateConstants.MEETING
      )
    );
  }
}
